import { useQueryClient } from '@tanstack/react-query';
import parse from 'html-react-parser';
import React, { useEffect, useId, useRef, useState, useCallback } from 'react';
import { Controller, type FieldValues, FormProvider, useForm } from 'react-hook-form';
import appConfig from '../../../config';
import type { InsetVariableType, Question } from '../../../config/types';
import content from '../../../content';
import { useFormData } from '../../../hooks/useFormData';
import type { FetchFormResponse } from '../../../service/types';
import { Button, Query } from '../../../types';
import { renderAnswer } from '../../../utils/config-utils';
import { getListAnswers } from '../../../utils/form-utils';
import ButtonText from '../../ButtonText';
import ErrorSummary from '../../ErrorSummary/ErrorSummary';
import { renderSummaryRow } from '../../SummaryAnswers/SummaryAnswers';
import { P } from '../../ui';
import AutocompleteWrapper from '../Autocomplete/AutocompleteWrapper';
import CheckboxGroup from '../CheckboxGroup/CheckboxGroup';
import { DateInputField } from '../DateInputField/DateInputField';
import { GradeInputField } from '../DateInputField/GradeInputField';
import { GradeInputFieldCoulmnwise } from '../DateInputField/GradeInputFieldCoulmnwise';
import { ShortDateInputField } from '../DateInputField/ShortDateInputField';
import { DisabilityInput } from '../DisabilityInput';
import DualInput from '../DualInput';
import { EthnicityInput } from '../EthnicityInput';
import { ExceptionBreakdownInput } from '../ExceptionBreakdownInput';
import Fieldset from '../Fieldset';
import FileUpload from '../FileUpload';
import { GenderInput } from '../GenderInput';
import { InputFairAndOpen } from '../InputFairAndOpen';
import { InputRecruitmentCampaigns } from '../InputRecruitmentCampaigns';
import Inset from '../Inset';
import PostcodeLookup, { validateManualAddress } from '../PostcodeLookup/PostcodeLookup';
import RadioGroup from '../RadioGroup/RadioGroup';
import RowsWithInputs from '../RowsWithInputs';
import SelectInputField from '../SelectInputField';
import TextInputField from '../TextInputField';
import Textarea from '../Textarea';
import TwoTextBox from '../TwoTextBox';
import CostInputField from '../CostInputField';
import InstanceInputField from '../InstanceInputField';
import type { Props } from './types';

enum Screen {
  ListPage = 'ListPage',
  QuestionPage = 'QuestionPage',
  ConfirmDeletePage = 'ConfirmDeletePage',
}

function QuestionPageTemplate({ config, onSave, onSaveUnfinished, errors, disabled, onContinue }: Props) {
  const id = useId();
  const { questions = [], pageType } = config;
  const [showSaveButton, setShowSaveButton] = useState(true);
  const [screen, setScreen] = useState(pageType === 'list' ? Screen.ListPage : Screen.QuestionPage);
  const [currentQuestions, setCurrentQuestions] = useState<Question[]>(questions);
  const { data } = useFormData();
  const methods = useForm<FetchFormResponse['answers']>({
    ...appConfig.form,
    defaultValues: useQueryClient().getQueryData<FetchFormResponse>([Query.FormData])?.answers,
  });
  const titleRef = useRef<HTMLHeadingElement>(null);

  const replaceInsetVariables = (
    content: string | undefined,
    variables: Record<string, { type: InsetVariableType; key: string }> | undefined,
  ) => {
    if (!content || !variables || !data?.answers) return content;
    let replacedContent = content;
    for (const [key, { type, key: answerKey }] of Object.entries(variables)) {
      if (type === 'previousAnswer') {
        const answer = Object.keys(data.answers).find((k) => k.endsWith(answerKey));
        if (answer) {
          replacedContent = replacedContent.replace(`\${${key}}`, data.answers[answer]);
        }
      }
    }
    return replacedContent;
  };

  useEffect(() => {
    for (const error of errors || []) {
      methods.setError(error.name, error.error, error.options);
    }
  }, [errors, methods.setError]);

  useEffect(() => {
    titleRef.current?.focus();
  }, [config]);

  const addQuestion = useCallback(
    (formValues: FieldValues) => {
      const questionIdPrefix =
        Object.keys(formValues)
          .filter((item) => item.endsWith(questions[0].name))
          .map((item) => Number(item.split('-')[0]))
          .reduce((max, current) => Math.max(max, current), 0) + 1;

      setCurrentQuestions(questions.map((q) => ({ ...q, name: `${questionIdPrefix}-${q.name}` })));
      setScreen(Screen.QuestionPage);
    },
    [questions],
  );

  useEffect(() => {
    if (pageType === 'list') {
      const formValues = methods.getValues();
      if (Object.entries(getListAnswers(formValues, questions)).length < 1) {
        addQuestion(formValues);
      }
    }
  }, [addQuestion, methods, pageType, questions]);

  async function handleDelete(data: FieldValues, deletedQuestions: Question[]) {
    const deletedQuestionNames = deletedQuestions.map((q) => q.name);
    const dataToSave = Object.fromEntries(
      Object.entries(data).map(([question, answer]) =>
        deletedQuestionNames.includes(question) ? [question, ''] : [question, answer],
      ),
    );
  
    console.log('Attempting delete save...');
    
    await onSave(dataToSave, methods.setError); // wait for save
    
    console.log('Delete save complete.');
  
    if (onContinue) {
      console.log('Navigating after delete...');
      onContinue();
    }
  }

  function renderListSummary(formValues: FieldValues, questions: Question[]) {
    const groupedAnswers = getListAnswers(formValues, questions);
    return (
      <>
        {Object.entries(groupedAnswers).map(([id, answers]) => (
          <React.Fragment key={id}>
            <tr className="govuk-table__row">
              <th className="govuk-table__cell">Answer {id}</th>
              <td className="govuk-table__cell" colSpan={3} style={{ height: '46px', textAlign: 'right' }}>
                <ButtonText
                  type="button"
                  style={{ fontSize: '19px', marginRight: '20px' }}
                  onClick={() => {
                    setCurrentQuestions(questions.map((q) => ({ ...q, name: `${id}-${q.name}` })));
                    setScreen(Screen.QuestionPage);
                  }}
                >
                  {content.buttonChange}
                </ButtonText>
                <ButtonText
                  type="button"
                  style={{ fontSize: '19px' }}
                  onClick={() => {
                    setCurrentQuestions(questions.map((q) => ({ ...q, name: `${id}-${q.name}` })));
                    setScreen(Screen.ConfirmDeletePage);
                  }}
                >
                  {content.buttonRemove}
                </ButtonText>
              </td>
            </tr>
            {answers.map((answer) => {
              const formattedAnswers = renderAnswer(answer.question, answer.answer);
              return (
                <React.Fragment key={answer.questionName}>
                  {renderSummaryRow(answer.question, formattedAnswers)}
                </React.Fragment>
              );
            })}
          </React.Fragment>
        ))}
      </>
    );
  }

  return (
    <>
      {/* List Page */}
      {screen === Screen.ListPage && (
        <FormProvider {...methods}>
          {(config.description ?? '').split('\n').map((paragraph) => (
            <P key={paragraph.substring(0, 20)}>{parse(paragraph)}</P>
          ))}
          <table className="govuk-table">
            <tbody className="govuk-table__body">{renderListSummary(methods.getValues(), questions)}</tbody>
          </table>
          <div className="gov-uk-button-group">
            <button
              className="govuk-button save-button"
              type="button"
              name="add"
              onClick={() => addQuestion(methods.getValues())}
              disabled={disabled}
            >
              {content.buttonAddAnother}
            </button>
            <button
              className="govuk-button govuk-button--secondary"
              type="button"
              name="continue"
              onClick={() => {
                if (onContinue) onContinue();
              }}
              disabled={disabled}
            >
              {content.buttonContinue}
            </button>
          </div>
        </FormProvider>
      )}

      {/* Confirm Delete Page */}
      {screen === Screen.ConfirmDeletePage && (
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit((data) => handleDelete(data, currentQuestions))}>
            <P>{content.confirmRemoval}</P>
            {currentQuestions.map((question) => (
              <P key={question.name}>
                <b>{question.title}</b>: {methods.getValues(question.name)}
              </P>
            ))}
            <div className="gov-uk-button-group">
              <button
                className="govuk-button govuk-button--warning delete-button"
                type="submit"
                name="delete"
                disabled={disabled}
              >
                {content.buttonConfirmRemove}
              </button>
              <button
                className="govuk-button govuk-button--secondary"
                type="button"
                name="cancel"
                onClick={() => setScreen(Screen.ListPage)}
                disabled={disabled}
              >
                {content.buttonCancelRemove}
              </button>
            </div>
          </form>
        </FormProvider>
      )}

      {/* Question Page */}
      {screen === Screen.QuestionPage && (
        <>
          <ErrorSummary errors={methods.formState.errors} idPrefix={id} />
        
          {(config.description ?? '').split('\n').map((paragraph) => (
            <P key={paragraph.substring(0, 20)}>{parse(paragraph)}</P>
          ))}
        
          <FormProvider {...methods}>
            <form
              onSubmit={methods.handleSubmit((data) => {
                if (pageType === 'list') {
                  sessionStorage.setItem('fromListPage', 'true');
                }
            
                onSave(data, methods.setError);
            
                if (pageType === 'list') {
                  setScreen(Screen.ListPage);
                }
              })}
            >

              {currentQuestions.map((question) => {
                const errorMessage = methods.formState.errors[question.name];
                const anchorId = `${id}${question.name}`;
                const legendId = `${id}legend`;
                const defaultValue = methods.getValues(question.name);
                const rules = question?.rules || {};
                // const rows = question?.tableData || {}
        
                return (
                  <div
                    className={`govuk-form-group ${errorMessage && 'govuk-form-group--error'}`}
                    key={question.title}
                  >
                    <Fieldset
                      title={
                        rules?.required === true ? question.title : `${question.title} (optional)`
                      }
                      hintText={question.hint}
                      legendId={legendId}
                      data-testid={`question-fieldset-${question.name}`}
                    >
                      <>
                        {question.questionType === 'radio' && (
                          <RadioGroup
                            anchorId={anchorId}
                            disabled={disabled}
                            question={question}
                            id={id}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'date' && (
                          <DateInputField
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'shortDate' && (
                          <ShortDateInputField
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {/* {question.questionType === 'gradeInput' && (
                                                    <GradeInputField
                                                        question={question}
                                                        anchorId={anchorId}
                                                        disabled={disabled}
                                                        detailsHeading={question.detailsHeading}
                                                        detailsContent={question.detailsContent}
                                                    />
                                                )} */}
                        {question.questionType === 'twoTextBox' && (
                          <TwoTextBox
                            title1={question.title}
                            title2="arul-title"
                            name1={question.name}
                            name2={`${question.name}hello`}
                            width="one-quarter"
                            rules1={rules}
                            rules2={rules}
                            defaultValue={defaultValue || ''}
                            errorMessages1={question.errorMessages}
                            errorMessages2={question.errorMessages}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {/* {question.questionType === 'grid' && <Grid5x5 />} */}
                        {question.questionType === 'select' && (
                          <div
                            className={`govuk-form-group ${
                              errorMessage && 'govuk-form-group--error'
                            }`}
                            key={question.name}
                            id={anchorId}
                          >
                            <SelectInputField
                              name={question.name}
                              id={anchorId}
                              aria-labelledby={legendId}
                              hintOption={question.hintOption}
                              options={question.options || []}
                              disabled={disabled}
                              rules={rules}
                              title={question.title}
                              errorMessages={question.errorMessages}
                              detailsHeading={question.detailsHeading}
                              detailsContent={question.detailsContent}
                            />
                          </div>
                        )}
                        {question.questionType === 'checkbox' && (
                          <CheckboxGroup
                            anchorId={anchorId}
                            disabled={disabled}
                            question={question}
                            id={id}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'textInput' && (
                          <TextInputField
                            aria-labelledby={legendId}
                            id={anchorId}
                            disabled={disabled}
                            title={question.title}
                            name={question.name}
                            rules={rules}
                            defaultValue={defaultValue || ''}
                            errorMessages={question.errorMessages}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                            autoFill={question.autoFill}
                            autofillMath={question.autofillMath}
                          />
                        )}
                        {question.questionType === 'textInputSmall' && (
                          <TextInputField
                            aria-labelledby={legendId}
                            id={anchorId}
                            disabled={disabled}
                            title={question.title}
                            name={question.name}
                            rules={rules}
                            width="one-third"
                            defaultValue={defaultValue || ''}
                            errorMessages={question.errorMessages}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                            autoFill={question.autoFill}
                            autofillMath={question.autofillMath}
                          />
                        )}
                        {question.questionType === 'inset' && (
                          <Inset
                            insetContent={replaceInsetVariables(
                              question.insetContent,
                              question.insetVariables,
                            )}
                            heading={'my heading'}
                          />
                        )}
                        {question.questionType === 'rowsWithInput' && (
                          <RowsWithInputs
                            aria-labelledby={legendId}
                            id={anchorId}
                            // tableData={question.tableData || { headers: [], rows: [] }}
                            disabled={disabled}
                            title={question.title}
                            name={question.name}
                            rules={rules}
                            defaultValue={defaultValue || ''}
                            errorMessages={question.errorMessages}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'dualInput' && (
                          <DualInput
                            aria-labelledby={legendId}
                            id={anchorId}
                            disabled={disabled}
                            title={question.title}
                            name={question.name}
                            name1="elan-name"
                            rules={rules}
                            defaultValue={defaultValue || ''}
                            errorMessages={question.errorMessages}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'grade' && (
                          <GradeInputField
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'gradeColumn' && (
                          <GradeInputFieldCoulmnwise
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'recruitmentCampaignInput' && (
                          <InputRecruitmentCampaigns
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'ethnicityInput' && (
                          <EthnicityInput
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'genderInput' && (
                          <GenderInput
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'disabilityInput' && (
                          <DisabilityInput
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'exceptionBreakdownInput' && (
                          <ExceptionBreakdownInput
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'fairAndOpenInput' && (
                          <InputFairAndOpen
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            label={question.label}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'textarea' && (
                          <Textarea
                            aria-labelledby={legendId}
                            numeric={!!question.rules?.number || undefined}
                            id={anchorId}
                            disabled={disabled}
                            title={question.title}
                            name={question.name}
                            rules={rules}
                            defaultValue={defaultValue || ''}
                            errorMessages={question.errorMessages}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'addressLookup' && (
                          <Controller
                            name={question.name}
                            control={methods.control}
                            rules={{
                              validate: validateManualAddress,
                            }}
                            render={({ field: { ref: _ref, ...field }, fieldState: { error } }) => (
                              <PostcodeLookup
                                {...field}
                                onValueSelect={(data) => methods.setValue(question.name, data)}
                                onValueChange={methods.setValue}
                                setShowSaveButton={setShowSaveButton}
                                defaultValue={defaultValue}
                                manualAddressError={error}
                                detailsHeading={question.detailsHeading}
                                detailsContent={question.detailsContent}
                              />
                            )}
                          />
                        )}
                        {question.questionType === 'autoComplete' && (
                          <AutocompleteWrapper
                            id={anchorId}
                            items={question.items || []}
                            name={question.name}
                            hintText={question.hint}
                            title={question.title}
                            rules={rules}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'fileUpload' && (
                          <FileUpload
                            id={anchorId}
                            name={question.name}
                            label={question.label}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'costInput' && (
                            <CostInputField
                                aria-labelledby={legendId}
                                id={anchorId}
                                disabled={disabled}
                                title={question.title}
                                name={question.name}
                                rules={rules}
                                txt={question.txt || ''}
                                defaultValue={defaultValue || ''}
                                errorMessages={question.errorMessages}
                                detailsHeading={question.detailsHeading}
                                detailsContent={question.detailsContent}
                            />
                        )}
                        {question.questionType === 'instanceInput' && (
                            <InstanceInputField
                                aria-labelledby={legendId}
                                id={anchorId}
                                disabled={disabled}
                                title={question.title}
                                name={question.name}
                                rules={rules}
                                txt={question.txt || ''}
                                defaultValue={defaultValue || ''}
                                errorMessages={question.errorMessages}
                                detailsHeading={question.detailsHeading}
                                detailsContent={question.detailsContent}
                            />
                        )}
                      </>
                    </Fieldset>
                  </div>
                );
              })}
        
              <div className="gov-uk-button-group">
                {showSaveButton && (
                  <button
                    className="govuk-button save-button"
                    type="submit"
                    name={Button.Save}
                    disabled={disabled}
                  >
                    {content.buttonSave}
                  </button>
                )}
                <button
                  className="govuk-button govuk-button--secondary"
                  type="button"
                  name={Button.SaveUnfinished}
                  onClick={() => {
                    methods.clearErrors();
                    onSaveUnfinished(methods.getValues());
                  }}
                  disabled={disabled}
                >
                  {content.buttonSaveUnfinished}
                </button>
              </div>
            </form>
          </FormProvider>
        </>
      )}
    </>
  );
}

export default QuestionPageTemplate;
